// `donation.completed.v1` — emitted by transactions-backend when a donation
// finalizes (Stripe payment_intent.succeeded or Finix equivalent).
//
// Marketing consumes this to fire PostDonationCoreWorkflow (Stage 3+).
// Transactions-backend is the PRODUCER once it joins the monorepo;
// during the migration window the legacy polyrepo transactions-backend
// publishes events with the same shape via the shared EventBus.
//
// detailType in EventBridge: `donation.completed.v1` (Commitment 6).

import { z } from 'zod';

import { eventEnvelopeSchema } from './base.js';

export const donationCompletedV1Schema = eventEnvelopeSchema.extend({
  donationId: z.string().min(1).max(256),
  // cents. NONnegative, not positive: $0 is a legitimate donation event — the
  // free-tickets handler is a first-class emitter and a free ticket is
  // `int(Decimal('0')*100) == 0`. `.positive()` would reject 0 and the bridge
  // would silently DROP every free-ticket donation from attribution. Negative
  // amounts are still rejected (a real producer bug).
  amount: z.number().int().nonnegative(),
  currency: z.string().length(3), // ISO 4217 (case not normalized; Step-3 consumers must compare case-insensitively — producers like Stripe emit lowercase)
  // Informational only — the donor is resolved downstream by `contactId`, not by
  // this field. Deliberately NOT `.email()`: a real-but-unusual address that
  // Zod's stricter regex rejects must never fail the whole-event parse and drop
  // the donation. Nullable for producers that don't capture an email.
  donorEmail: z.string().nullable(),
  formId: z.string().min(1).max(256).nullable(),
  // contactsdb contact id — lets marketing resolve the donor's
  // pocketflow_customer_id and fire "when a customer donates" triggers
  // (POST /businesses/{id}/track). Optional/nullable for forward-compat
  // with producers that can't resolve a contact; the workflow no-ops then.
  contactId: z.string().min(1).max(256).nullable().optional(),
  // True on the donor's first-ever donation so marketing can ALSO emit the
  // `contact.donates_first` project-defined event. Producer-computed;
  // treated as false when absent.
  isFirstDonation: z.boolean().optional(),

  // ── Attribution fields (PE-1519). All ADDITIVE + OPTIONAL → still `.v1`
  // (non-breaking): existing producers that omit them keep validating, and
  // the schema is parsed non-strictly so it never rejects an older payload.
  // The donation hot path stamps these so marketing's attribution workflow
  // (Step 3) is self-sufficient and never re-loads the transaction row (D4).

  // UTM tags captured at donation time. Feeds Layer-1 attribution: carries
  // `pfCampaignID` / `pfTriggerID` matched against pf_broadcasts. Free-form
  // map so producers can forward whatever they captured; inner VALUES are
  // nullable too so a producer that serializes an unknown tag value as `null`
  // can't fail the whole-event parse and get the donation silently dropped.
  utmTags: z.record(z.string().nullable()).nullable().optional(),
  // Whether this donation is recurring (`donationFrequency != 'ONE_TIME'`).
  // Producer-computed; treated as false when absent.
  isRecurring: z.boolean().optional(),
  // The recurring subscription this donation belongs to, when recurring.
  // Used downstream by the future exclude-pre-existing-recurring rule; the
  // rule derives from this — marketing does not look it up. No `.min(1)`:
  // see paymentMethod below for why empty strings must not drop the event.
  subscriptionId: z.string().max(256).nullable().optional(),
  // Payment method. Kept a free string (NOT a z.enum) on purpose: this event
  // gates a money-attribution path, and the producer is a separate repo — a
  // strict enum would make the bridge DROP a valid donation the moment a new
  // method ships. Deliberately permissive on emptiness too — nullable AND no
  // `.min(1)` — so a producer that serializes an unknown value as `null` OR as
  // `""` can't fail the whole-event parse and get the donation silently
  // dropped. Known values: CARD | ACH | APPLE_PAY | GOOGLE_PAY | PAYPAL | FREE.
  paymentMethod: z.string().max(64).nullable().optional(),
  // Payment processor that settled the donation. Free string, nullable, no
  // `.min(1)` — same never-silently-drop reasoning as paymentMethod. Known
  // values: STRIPE | FINIX | PAYPAL.
  paymentProcessor: z.string().max(64).nullable().optional(),
  // ── Recurring-donor milestones (PE-1649). Producer-computed via the
  // donations ContactIdIndex history check (+ a subscriptions-table status
  // check for `resumesRecurring`); at most ONE is true per event, both are
  // OMITTED (not false) when the producer can't resolve an answer, and both
  // are treated as false when absent. Renewals and added-while-still-active
  // subscriptions carry neither.
  //
  // True when this donation created the donor's FIRST-ever subscription —
  // marketing emits `contact.first_time_recurring` (recurring-donor welcome)
  // and suppresses the generic `contact.donates_first`.
  createsFirstSubscription: z.boolean().optional(),
  // True when this donation starts a NEW subscription for a donor whose prior
  // subscriptions have all ended (none ACTIVE/ERROR) — they became a recurring
  // donor AGAIN. Marketing emits `contact.becomes_recurring_again`.
  resumesRecurring: z.boolean().optional(),
});

export type DonationCompletedV1 = z.infer<typeof donationCompletedV1Schema>;

export const DONATION_COMPLETED_V1_DETAIL_TYPE = 'donation.completed.v1' as const;
